/**
 * Browsing filters (issue #15): what the Library and the pickers apply on top of whatever set
 * of Entries they start from (a Pack, the whole catalogue, a Choice's legal candidates). Pure
 * functions over a small view of each item, so IndexRecords and candidate shapes both fit.
 */
import type { IndexRecord } from "../../shared/snapshot-format";
import type { Search } from "./search";

/** What the filters read from an item, as accessors so nothing is allocated per item per keystroke. */
export interface BrowseAdapter<T> {
  uuid(item: T): string;
  name(item: T): string;
  level(item: T): number | null;
  traits(item: T): readonly string[];
  rarity(item: T): string;
}

export type SortOrder = "name" | "level";

export interface EntryFilter {
  text: string;
  levelMin: number | null;
  levelMax: number | null;
  /** Every listed trait must be present. */
  traits: string[];
  /** Empty means any rarity. */
  rarities: string[];
  sort: SortOrder;
}

export const EMPTY_FILTER: Readonly<EntryFilter> = Object.freeze({ text: "", levelMin: null, levelMax: null, traits: [], rarities: [], sort: "name" });

export const RARITIES = ["common", "uncommon", "rare", "unique"] as const;

export const recordAdapter: BrowseAdapter<IndexRecord> = { uuid: (r) => r.u, name: (r) => r.n, level: (r) => r.l, traits: (r) => r.tr, rarity: (r) => r.r };

/** True when the filter narrows anything beyond the text. */
export function hasFacets(f: EntryFilter): boolean {
  return f.levelMin !== null || f.levelMax !== null || f.traits.length > 0 || f.rarities.length > 0;
}

function passes<T>(item: T, a: BrowseAdapter<T>, f: EntryFilter, facets: boolean): boolean {
  if (!facets) return true;
  const level = a.level(item);
  if (f.levelMin !== null && (level === null || level < f.levelMin)) return false;
  if (f.levelMax !== null && (level === null || level > f.levelMax)) return false;
  if (f.rarities.length > 0 && !f.rarities.includes(a.rarity(item))) return false;
  if (f.traits.length > 0) {
    const traits = a.traits(item);
    for (const t of f.traits) if (!traits.includes(t)) return false;
  }
  return true;
}

const collator = new Intl.Collator("en", { sensitivity: "base", numeric: true });

/**
 * Apply the filter to `items`. With text and a search, the order is the search's ranking
 * (exact names first) restricted to `items`; with text and no search, a name prefix then
 * substring match in name order; without text, the chosen sort.
 */
export function browseEntries<T>(items: readonly T[], filter: EntryFilter, search: Search | null, a: BrowseAdapter<T>): T[] {
  const needle = filter.text.trim().toLowerCase();
  const facets = hasFacets(filter);
  const byName = (x: T, y: T): number => collator.compare(a.name(x), a.name(y));
  const byLevel = (x: T, y: T): number => (a.level(x) ?? -1) - (a.level(y) ?? -1) || byName(x, y);
  if (search && needle) {
    // Walk the ranked hits, keeping the order, and admit those in `items` that pass the facets.
    const members = new Map<string, T>();
    for (const item of items) members.set(a.uuid(item), item);
    const out: T[] = [];
    for (const h of search.query(needle)) {
      const item = members.get(h.record.u);
      if (item !== undefined && passes(item, a, filter, facets)) out.push(item);
    }
    return out;
  }
  const kept: T[] = [];
  for (const item of items) if (passes(item, a, filter, facets)) kept.push(item);
  if (!needle) return kept.sort(filter.sort === "level" ? byLevel : byName);
  const prefix: T[] = [];
  const contains: T[] = [];
  for (const item of kept) {
    const name = a.name(item).toLowerCase();
    if (name.startsWith(needle)) prefix.push(item);
    else if (name.includes(needle) || a.traits(item).some((t) => t.includes(needle))) contains.push(item);
  }
  return [...prefix.sort(byName), ...contains.sort(byName)];
}

/** The traits present across `items`, most frequent first, for a trait picker. */
export function traitFacets<T>(items: readonly T[], a: BrowseAdapter<T>): { trait: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const item of items) for (const t of a.traits(item)) counts.set(t, (counts.get(t) ?? 0) + 1);
  return [...counts].map(([trait, count]) => ({ trait, count })).sort((a, b) => b.count - a.count || a.trait.localeCompare(b.trait));
}

/** The Library's outer filters over the catalogue, applied before the EntryFilter. */
export interface CatalogueScope {
  pack: string | null;
  type: string | null;
  /** A feat category (class, ancestry, skill, ...), when the type is feat. */
  category: string | null;
  publication: string | null;
  legacy: boolean;
}

export const EMPTY_SCOPE: Readonly<CatalogueScope> = Object.freeze({ pack: null, type: null, category: null, publication: null, legacy: false });

export function inScope(r: IndexRecord, s: CatalogueScope): boolean {
  if (!s.legacy && !r.rm) return false;
  if (s.pack !== null && r.p !== s.pack) return false;
  if (s.type !== null && r.t !== s.type) return false;
  if (s.category !== null && r.c !== s.category) return false;
  if (s.publication !== null && r.pub !== s.publication) return false;
  return true;
}

export function scopeCatalogue(records: readonly IndexRecord[], s: CatalogueScope): IndexRecord[] {
  return records.filter((r) => inScope(r, s));
}

/** Distinct values of one field across records, with counts, most frequent first. */
export function fieldFacets(records: readonly IndexRecord[], field: "t" | "c" | "pub"): { value: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const r of records) {
    const v = r[field];
    if (typeof v === "string" && v) counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  return [...counts].map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}
